// @spec AUD-STR-002
// @spec AUD-STR-003
// @spec AUD-STR-004
// @spec AUD-STR-015
// @spec AUD-STR-041
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import * as React from 'react';
import { z } from 'zod';

import { AuditLogFilters } from '@/features/audit/components/AuditLogFilters';
import { AuditLogTable } from '@/features/audit/components/AuditLogTable';
import { UserActivityReportButton } from '@/features/audit/components/UserActivityReportButton';
import { VerifyIntegrityButton } from '@/features/audit/components/VerifyIntegrityButton';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

// URL search params schema for validation
const auditLogSearchSchema = z.object({
  search: z.string().optional().default(''),
  modules: z
    .union([z.array(z.string()), z.string()])
    .optional()
    .transform((val) => {
      if (!val) return [];
      return Array.isArray(val) ? val : [val];
    }),
});

export const Route = createFileRoute('/_authenticated/settings/audit-log')({
  validateSearch: auditLogSearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.auditLog'),
  }),
  component: AuditLogPage,
});

/**
 * Organization audit log page displaying security and operational events.
 *
 * Features:
 * - Full-text search across audit events
 * - Module filter to filter by platform domain
 * - URL state persistence for shareable filtered views
 * - Real-time event streaming with 5-second polling
 * - Paginated table with 100 events per page
 * - Accessible to org:admin and org:compliance_officer roles
 */
function AuditLogPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate({ from: Route.fullPath });
  const { search, modules } = Route.useSearch();

  const handleSearchChange = React.useCallback(
    (newSearch: string) => {
      void navigate({
        search: { search: newSearch, modules },
        replace: true,
      });
    },
    [navigate, modules]
  );

  const handleModulesChange = React.useCallback(
    (newModules: string[]) => {
      void navigate({
        search: { search, modules: newModules },
        replace: true,
      });
    },
    [navigate, search]
  );

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('auditLogPage.title')}</h1>
          <p className="mt-1 text-muted-foreground">{t('auditLogPage.description')}</p>
        </header>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <AuditLogFilters
            search={search}
            onSearchChange={handleSearchChange}
            modules={modules}
            onModulesChange={handleModulesChange}
          />
          <div className="flex items-center gap-2">
            <UserActivityReportButton />
            <VerifyIntegrityButton />
          </div>
        </div>

        <AuditLogTable
          search={search}
          filters={{ resourceDomain: modules.length > 0 ? modules : undefined }}
        />
      </div>
    </div>
  );
}
